import { defineStore } from 'pinia'
import backendHandler from '@/BackendAPI'
import { useInstanceStore } from '@/store/backendInstances'

export const useMeasurementStore = defineStore('measurements', {
  state: () => ({}),
  persist: false,
  share: {
    enable: false,
    initialize: false,
  },
  getters: {},
  actions: {
    async loadUsedLabels() {
      const response = await backendHandler.get(useInstanceStore().selectedInstanceId, { path: '/api/data/labels' })
      if (!response.ok) return []
      return response.data
    },

    async loadGenericData(path, query, incidentReductionSettings, abortSignal) {
      const dataResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: path,
        data: {
          iReadTheDocumentationAndReallyWantToRequestAllThatData: true,
          query: query,
          incidentReduction: incidentReductionSettings,
          timeFormat: incidentReductionSettings ? 'ms' : undefined,
        },
        isPathBound: true,
        signal: abortSignal,
      })
      if (!dataResponse.ok) return null
      return dataResponse.data
    },

    async loadGenericMeasurement(measurement, query, abortSignal) {
      const dataResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/data/' + measurement,
        data: {
          iReadTheDocumentationAndReallyWantToRequestAllThatData: true,
          query: query,
        },
        isPathBound: true,
        signal: abortSignal,
      })
      if (!dataResponse.ok) return null
      return dataResponse.data
    },

    async loadSimpleMeasurementForSingleCamera(measurement, value, cameraId, timeQuery, timeResolution, xAggregation = 'mean') {
      return this.loadGenericMeasurement(measurement, {
        columns: { [value]: `${xAggregation}@y`, time: true },
        where: { camera: cameraId },
        time: timeQuery,
        timeResolution: timeResolution + 'ms',
      })
    },
    loadSimpleMeasurementForMultipleBarns(
      measurement,
      value,
      vmsInstanceNames,
      timeQuery,
      timeResolution,
      aggregate = true,
      xAggregation = 'mean',
      where
    ) {
      return this.loadGenericMeasurement(measurement, {
        columns: { [value]: `${xAggregation}@y`, time: true },
        where: { vmsInstanceName: vmsInstanceNames, ...where },
        time: timeQuery,
        timeResolution: timeResolution + 'ms',
        groupByTags: aggregate === true ? undefined : 'vmsInstanceName',
      })
    },
    async loadRegionSumData(timeQuery, timeResolution, property, specificValues = undefined, aggregate = true, abortSignal = undefined) {
      const query = {
        columns: { meanCount: 'mean@y', time: true },
        time: timeQuery,
        timeResolution: timeResolution + 'ms',
        groupByTags: aggregate ? undefined : 'value',
        where: { property: property },
      }
      if (specificValues?.length) {
        query.where.value = specificValues.specificValues
      }
      return this.loadGenericMeasurement('regionSums', query, abortSignal)
    },

    async loadLabelCountsForMultipleCameras(
      cameraIds,
      timeQuery,
      timeResolution,
      labels = undefined,
      aggregateCameras = true,
      aggregateLabels = true,
      abortSignal = undefined
    ) {
      const dataResponse = await backendHandler.post(useInstanceStore().selectedInstanceId, {
        path: '/api/data/labelCounts',
        data: {
          iReadTheDocumentationAndReallyWantToRequestAllThatData: true,
          labels,
          time: timeQuery,
          timeResolution: timeResolution + 'ms',
          groupByLabel: !aggregateLabels,
          groupByCamera: !aggregateCameras,
          cameras: cameraIds,
        },
        isPathBound: true,
        signal: abortSignal,
      })
      if (!dataResponse.ok) return null
      return dataResponse.data
    },

    async loadGridScoreDerivationMeasurement(timeQuery, timeResolution, cameraIds, aggregateCameras = true, abortSignal) {
      const query = [
        {
          columns: { gridScore: 'spread@inner' },
          time: timeQuery,
          timeResolution: '10m',
          groupByTags: ['camera'],
        },
        {
          columns: { inner: 'max@y', time: true },
          timeResolution: timeResolution + 'ms',
        },
      ]
      if (cameraIds) {
        query[0].where = { camera: cameraIds }
      }
      if (!aggregateCameras) {
        query[1].groupByTags = ['camera']
      }
      return this.loadGenericMeasurement('distribution', query, abortSignal)
    },

    async loadRegionCounts(
      timeQuery,
      timeResolution,
      labels = undefined,
      aggregateLabels = true,
      regionType = undefined,
      regionSpecifications = undefined,
      groupByPen = false,
      abortSignal = undefined
    ) {
      const query = [
        {
          columns: { regions: 'sum@inner' },
          time: timeQuery,
          timeResolution: '1m',
          groupByTags: [regionType],
          where: { [regionType]: regionSpecifications },
        },
        {
          columns: { inner: 'mean@y', time: true },
          timeResolution: timeResolution + 'ms',
          groupByTags: [regionType],
          where: {},
        },
      ]
      if (!aggregateLabels) {
        query[0].groupByTags.push('label')
        query[1].groupByTags.push('label')
      }
      if (groupByPen) {
        query[0].groupByTags.push('pen')
        query[1].groupByTags.push('pen')
      }
      if (labels) {
        query[0].where.label = labels
      }

      return this.loadGenericMeasurement('regions', query, abortSignal)
    },
    async loadAvailableMeasurements(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/data/showAvailableMeasurements' })
      if (!response.ok) return []
      return response.data
    },

    async loadAvailableFieldsForMeasurement(globalUniqueIdentifier = useInstanceStore().selectedInstanceId, measurement) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/data/showFieldsForMeasurement/' + measurement })
      if (!response.ok) return []
      return response.data
    },

    async loadAvailableTagKeyForMeasurement(globalUniqueIdentifier = useInstanceStore().selectedInstanceId, measurement) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/data/showTagKeysForMeasurement/' + measurement })
      if (!response.ok) return []
      return response.data
    },

    async loadAvailableTagValuesForMeasurement(globalUniqueIdentifier = useInstanceStore().selectedInstanceId, measurement, tagKey) {
      const response = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/data/showTagValuesForMeasurementAndTagKey/' + measurement + '/' + tagKey,
      })
      if (!response.ok) return []
      return response.data
    },
  },
})
